import { useState, useEffect, useCallback } from 'react';
import GpuQuantityCell from 'components/Layout/Mining/MiningCalculator/GpuQuantityCell/GpuQuantityCell';
import RevenueCell from 'components/Layout/Mining/MiningCalculator/RevenueCell/RevenueCell';
import StaticTable from 'components/StaticTable';
import useOnLocalEvent from 'hooks/useOnLocalEvent';
import { useTranslation } from 'next-i18next';
import Head from 'next/head';
import { IGpuState } from 'types/GpuData';
import ModelCell from './ModelCell/ModelCell';
import PageHero from 'components/Layout/PageHero';
import Summary from './Summary';
import localEventNames from '@simplemining/dashboard/src/helpers/localEventNames/chips';

const defaultPowerCost = '0.08';
const defaultHashrate = 100;

const Calculator = (props: ICalculator) => {
  const {
    coinData,
    chipData,
    children,
    title,
    heading,
    metaKeywords,
    metaDescription,
    type,
  } = props;

  const [powerCost, setPowerCost] = useState<string>(defaultPowerCost);
  const [isCalculatorModifiedByUser, setIsCalculatorModifiedByUser] =
    useState<boolean>(true);
  const { t } = useTranslation();

  const [chipsState, setGpusState] = useState<IGpuState[]>();

  const revenuePerDefaultHashrate = Number(
    coinData?.revenue.replace(/[^0-9]/, ''),
  );

  const getCalculatedProfit = useCallback(
    (props: IGetCalculatedProfit) => {
      const { chip, quantity, energyCost } = props;

      const roundedCost = Math.round(+energyCost * 100) / 100;

      const calculatedProfit =
        ((chip.hash * revenuePerDefaultHashrate) / defaultHashrate -
          (chip.watts / 1000) * roundedCost * 24) *
        (quantity || 1);

      return calculatedProfit;
    },
    [revenuePerDefaultHashrate],
  );

  const getCalculatedRevenue = useCallback(
    (props: IGetCalculatedRevenue) => {
      const { chip, quantity } = props;

      return (
        ((chip.hash * revenuePerDefaultHashrate) / defaultHashrate) *
        (quantity || 1)
      );
    },
    [revenuePerDefaultHashrate],
  );

  const getCalculatedGpu = useCallback(
    (props: IGetCalculatedGpu) => {
      const { chip, quantity = 0, energyCost } = props;

      return {
        ...chip,
        quantity,
        revenue: getCalculatedRevenue({ chip, quantity }),
        profit: getCalculatedProfit({ chip, quantity, energyCost }),
      };
    },
    [getCalculatedProfit, getCalculatedRevenue],
  );

  const handleGpusQuantityChange = (data: IHandleGpusQuantityChange) => {
    const { quantity, row } = data;

    setGpusState(prevState => {
      return prevState?.map(chip => {
        if (chip.id === row.id) {
          return getCalculatedGpu({
            chip: row,
            quantity,
            energyCost: powerCost,
          });
        }

        return chip;
      });
    });
  };

  const handlePowerCostChange = (data: IHandlePowerCostChange) => {
    const { energyCost } = data;

    setPowerCost(energyCost);

    setGpusState(prevState => {
      return prevState?.map(chip => {
        return {
          ...chip,
          profit: getCalculatedProfit({
            chip,
            energyCost,
            quantity: chip.quantity,
          }),
        };
      });
    });
  };

  useOnLocalEvent({
    eventName: localEventNames.chipsQuantity,
    onAfterSuccess: handleGpusQuantityChange,
  });

  useOnLocalEvent({
    eventName: localEventNames.chipsPowerCost,
    onAfterSuccess: handlePowerCostChange,
  });

  const hashUnits = {
    asic: 'TH/s',
    gpu: 'MH/s',
  };

  const hashUnit = hashUnits[type] || '';

  const columns = [
    {
      header: { title: t('miningCalculator.table.number.title') },
      render: ({ rowIndex }) => (
        <div className="mining-calculator__hashrate-number">
          {rowIndex + 1 + '.'}
        </div>
      ),
    },
    {
      header: {
        title: type.toUpperCase(),
        description: t('miningCalculator.table.model.description'),
      },
      accessor: 'model',
      render: rowData => <ModelCell rowData={rowData} />,
    },
    {
      header: {
        title: t('miningCalculator.table.hash.title'),
        description: hashUnit,
      },
      headerClassName: 'mining-calculator__hashrate-cell-wrapper',
      accessor: 'hash',
      render: ({ accessor }) => (
        <div className="mining-calculator__hashrate-cell">
          {accessor ? `${accessor.toFixed(2)} ${hashUnit}` : 'N/D'}
        </div>
      ),
    },
    {
      header: {
        title: t('miningCalculator.table.watts.title'),
        description: t('miningCalculator.table.watts.description'),
      },
      headerClassName: 'mining-calculator__power-cell-wrapper',
      accessor: 'watts',
      render: ({ accessor }) => (
        <div className="mining-calculator__power-cell">
          {accessor ? `${accessor} W` : 'N/D'}
        </div>
      ),
    },
    {
      header: {
        title: t('miningCalculator.table.quantity.title'),
        description: t('miningCalculator.table.quantity.description', {
          type: type.toUpperCase(),
        }),
      },
      render: ({ row }) => <GpuQuantityCell row={row} state={chipsState} />,
    },
    {
      header: {
        title: t('miningCalculator.table.profitability.day.title'),
        description: t('miningCalculator.table.profitability.day.description'),
      },
      render: ({ row }) => (
        <RevenueCell
          row={row}
          state={chipsState}
          isCalculatorModifiedByUser={isCalculatorModifiedByUser}
          period="daily"
        />
      ),
    },
    {
      header: {
        title: t('miningCalculator.table.profitability.month.title'),
        description: t(
          'miningCalculator.table.profitability.month.description',
        ),
      },
      render: ({ row }) => (
        <RevenueCell
          row={row}
          state={chipsState}
          isCalculatorModifiedByUser={isCalculatorModifiedByUser}
          period="monthly"
        />
      ),
    },
  ];

  useEffect(() => {
    setGpusState(
      chipData
        ?.map(chip => getCalculatedGpu({ chip, energyCost: defaultPowerCost }))
        .sort((a, b) => b.revenue - a.revenue),
    );
  }, [getCalculatedGpu, chipData]);

  useEffect(() => {
    const sumOfUserGpus = chipsState?.reduce((acc, curr) => ({
      ...acc,
      quantity: acc.quantity + curr.quantity,
    })).quantity;

    if (sumOfUserGpus) {
      setIsCalculatorModifiedByUser(true);

      return;
    }

    setIsCalculatorModifiedByUser(false);
  }, [chipsState]);

  const totalProfit =
    chipsState?.reduce(
      (acc, curr) => {
        if (curr.quantity > 0) {
          return {
            profit: acc.profit + curr.profit,
          };
        }

        return { profit: acc.profit };
      },
      { profit: 0 },
    ).profit || 0;

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta
          name="keywords"
          content={t(`miningCalculator.${type}.meta.keywords`, {
            metaKeywords,
          }).trim()}
        />
        <meta name="description" content={metaDescription} />
      </Head>
      <>
        <div className="mining-calculator">
          <PageHero
            className="section-title"
            header={heading}
            description={t(`miningCalculator.${type}.heading.description`)}
            page="mining"
          >
            <p className="mining-calculator__header-disclaimer">
              {t('miningCalculator.whattomineDisclaimer')}
              <a
                href="https://whattomine.com/"
                target="_blank"
                rel="noreferrer"
                className="mining-calculator__header-disclaimer-link link-type-underline"
              >
                WhatToMine
              </a>
            </p>
          </PageHero>
          <Summary value={powerCost} name={coinData?.name} />
          <StaticTable
            data={chipsState}
            columns={columns}
            totalProfit={totalProfit}
            isCalculatorModifiedByUser={isCalculatorModifiedByUser}
          />
        </div>
        {children}
      </>
    </>
  );
};

export default Calculator;
